import React, { memo, useEffect, useState } from 'react';
import {
  Accordions,
  AccordionItem,
  AccordionItemHeader,
  AccordionItemBody,
} from 'appkit-react';
import { Progress } from 'antd';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import Actions from '@flux/action';
import AccessIndicator from '@components/accessIndicator';
import { getSessionStorage } from '@utils/windowUtil';
import { formatDateToEDTRrEST } from '@utils/widget';
import { generateProjectUrlSearch } from '@utils/common';
import { ComplexityLevelIndicator } from './complexity';
import {
  USER_PERMISSION_MODEL,
  USER_ROLE_MODEL,
  QUES_SECTIONS_SEQ,
  SECTIONS_LENGTH,
  COMPLEXITY_LEVEL_INDEX_MAP,
  QUES_SECTIONS_DESCRIPTION,
  QUESTION_STATUS,
  projectRole,
  // percentToLevel,
} from '@model';
import useStep from './useQuestionSteps';
// import CustomizationModal from './pdf/pdfCustomization';

const MENU_KEYS = {
  QUES: 'ques',
  COMPLEX: 'comp',
};

const QuestionnaireMenu = memo(function ({
  lastEditedBy,
  lastEditedAt,
  isClient,
}) {
  const isEdited = lastEditedBy && lastEditedAt;

  if (!isEdited) {
    return (
      <div className="initial-title">
        Diagnostic Questionnaire
        {!isClient && (
          <AccessIndicator page={USER_PERMISSION_MODEL.QUESTIONNAIRE.welcome} />
        )}
      </div>
    );
  }

  return (
    <>
      <div className="title">
        Diagnostic Questionnaire
        {!isClient && (
          <AccessIndicator page={USER_PERMISSION_MODEL.QUESTIONNAIRE.welcome} />
        )}
      </div>
      <div className="last-edit">
        {formatDateToEDTRrEST(new Date(lastEditedAt), lastEditedBy)}
      </div>
    </>
  );
});

const SiderLevelIndicator = ({ level, percent, noIndicator }) =>
  noIndicator ? null : level ? (
    <ComplexityLevelIndicator level={level} percent={percent} size="small" />
  ) : (
    <span className="no-level-indicator">In Progress</span>
  );

const ComplexityMenu = ({ level, title, percent, isClient, noIndicator }) => (
  <div className="initial-title complexity-title">
    <div className="complexity-overall-title">
      {title || 'Overall Deal Complexity'}
      {!isClient && (
        <AccessIndicator
          page={USER_PERMISSION_MODEL.QUESTIONNAIRE.complexity}
        />
      )}
    </div>
    <SiderLevelIndicator
      noIndicator={noIndicator}
      level={level}
      percent={percent}
    />
  </div>
);

function SideProgressSection({
  currentSection,
  isExpanded,
  canSeeQues,
  canSeeComplex,
}) {
  const history = useHistory();
  const steps = useStep(false);
  const location = useLocation();
  const dispatch = useDispatch();
  // const [isCustomModalVisible, setIsCustomModalVisible] = useState(false);

  const activeMenuKey = location.pathname.includes('complexity')
    ? MENU_KEYS.COMPLEX
    : location.pathname.includes('export-pdf')
    ? MENU_KEYS.COMPLEX
    : MENU_KEYS.QUES;

  const {
    status,
    attributes = {},
    complexitySiderData = {},
    projectId,
    isClient,
    QSaccessData,
  } = useSelector(({ question, common, projects, login }) => ({
    status: question.status,
    attributes: question.attributes,
    complexitySiderData: question.complexitySiderData,
    projectId: common.currentId,
    isClient:
      login.userInfo.role === USER_ROLE_MODEL.CLIENT ||
      common.projectCompanyInfo?.currentUserPermission ===
        projectRole['View-Only'],
    QSaccessData: question.QSaccessData,
  }));

  const canSeeReviewResponses =
    Object.values(status)
      .map((status_arr) => {
        const v = status_arr.filter(
          (status) =>
            ![QUESTION_STATUS.Finish, QUESTION_STATUS.NotApplicable].includes(
              status,
            ),
        );
        return v.length <= 0;
      })
      .filter((a) => a).length > 0;

  const items = QUES_SECTIONS_SEQ.map((section, i) => {
    const totalLength = SECTIONS_LENGTH[i];
    const currentFinishedCount = status[section]?.filter((item) =>
      [2, 3].includes(item),
    ).length;

    const percent = (currentFinishedCount / totalLength) * 100;

    const isFinished = totalLength === currentFinishedCount;
    const title = QUES_SECTIONS_DESCRIPTION[section]?.title;
    return (
      <AccordionItemBody
        key={`ques-sider-${section} `}
        onClick={() => {
          if (!QSaccessData?.[section]?.disabled) {
            history.push(
              `/questionnaires/${section}?${generateProjectUrlSearch(
                projectId,
              )}`,
            );
          }
        }}
        className={currentSection === section ? 'activated' : ''}
      >
        <div className="section-side-item">
          {isFinished && !QSaccessData?.[section]?.disabled ? (
            <span className="appkiticon icon-circle-checkmark-fill" />
          ) : (
            <Progress
              type="circle"
              showInfo={false}
              percent={QSaccessData?.[section]?.disabled ? 0 : percent}
              strokeLinecap="square"
              strokeColor="#49A44D"
              trailColor="#2c2c2c"
              width={24}
              strokeWidth={15}
            />
          )}

          <div className="section-side-item-text-wrapper">
            <span
              className="section-side-item-name"
              style={title.length > 25 ? { flexBasis: '65%' } : {}}
            >
              {title}
            </span>
            &ensp;
            <span>
              {!QSaccessData?.[section]?.disabled ? currentFinishedCount : 0}/
              {totalLength}
            </span>
          </div>
        </div>
        {/*{(section === 'enterprise' || section === 'execution') && (*/}
        {/*  <div className="opt-in-out">*/}
        {/*    <Switch*/}
        {/*      disabled={location.pathname.includes('export-pdf')}*/}
        {/*      checked={!QSaccessData?.[section]?.disabled}*/}
        {/*      onChange={async (bool, el) => {*/}
        {/*        el.stopPropagation();*/}
        {/*        if (!bool) {*/}
        {/*          steps.resetSection();*/}
        {/*        }*/}

        {/*        await API.postQuestionnaireAccess(*/}
        {/*          { [section]: { disabled: !bool } },*/}
        {/*          projectId,*/}
        {/*        );*/}
        {/*        const result = await API.getQuestionnaireAccess(projectId);*/}
        {/*        dispatch(Actions.getComplexitySiderData());*/}
        {/*        dispatch(Actions.questionnaireAccessHandler(result));*/}
        {/*      }}*/}
        {/*    />*/}
        {/*    <span>*/}
        {/*      {!QSaccessData?.[section]?.disabled ? 'Opt-in' : 'Opt-out'}*/}
        {/*    </span>*/}
        {/*  </div>*/}
        {/*)}*/}
      </AccordionItemBody>
    );
  });

  const handleAccordionChange = (e) => {
    if (
      e === MENU_KEYS.COMPLEX &&
      location.pathname !== '/questionnaires/complexity' &&
      complexitySiderData.abled
    ) {
      history.push(
        `/questionnaires/complexity${
          complexitySiderData.clickableSectionPaths[0] || ''
        }`,
      );
    }
  };

  const getComplexItems = () => {
    const arr = [];
    QUES_SECTIONS_SEQ.forEach((section) => {
      if (!QSaccessData[section]?.disabled) {
        arr.push(section);
      }
    });
    const complexityItem = arr.map((section, i) => {
      const { level: resLevel, levelPercent: percent } =
        complexitySiderData.complexities[section] || {};
      const level = COMPLEXITY_LEVEL_INDEX_MAP[resLevel];
      const handleRedirectToSection = () => {
        if (!level) {
          return;
        }
        history.push(
          `/questionnaires/complexity/${section}?${generateProjectUrlSearch(
            projectId,
          )}`,
        );
      };
      return (
        <AccordionItemBody
          key={`ques-sider-${section} `}
          onClick={handleRedirectToSection}
          className={`${
            window.location.pathname.includes(section) ? 'activated' : ''
          } ${level ? '' : 'disabled'}`}
        >
          <div className={`complexity-title ${level ? '' : 'disabled'}`}>
            <div className="complexity-title-text-wrapper">
              <span className="complexity-title-text">
                {QUES_SECTIONS_DESCRIPTION[section]?.title} Complexity
              </span>
              <SiderLevelIndicator level={level} percent={percent || 0} />
            </div>
          </div>
        </AccordionItemBody>
      );
    });
    // const exportItem = (
    //   <AccordionItemBody
    //     key="ques-sider-99"
    //     onClick={() => setIsCustomModalVisible(true)}
    //     className={`${
    //       window.location.pathname.includes('export-pdf') ? 'activated' : ''
    //     }`}
    //   >
    //     <div className="complexity-title">
    //       <div style={{ display: 'flex', justifyContent: 'space-between' }}>
    //         <span style={{ color: '#E45C2B ' }}>EXPORT TO PDF</span>
    //       </div>
    //     </div>
    //     <CustomizationModal
    //       visible={isCustomModalVisible}
    //       closeModal={() => setIsCustomModalVisible(false)}
    //       afterLoaded={() => {}}
    //     />
    //   </AccordionItemBody>
    // );

    // complexityItem.push(exportItem);
    return complexityItem;
  };

  const renderComplexityMenu = () =>
    complexitySiderData.abled ? (
      <AccordionItem itemId={MENU_KEYS.COMPLEX}>
        <AccordionItemHeader
          className={
            window.location.pathname === '/questionnaires/complexity'
              ? 'activated'
              : ''
          }
        >
          <ComplexityMenu
            level={
              COMPLEXITY_LEVEL_INDEX_MAP[
                complexitySiderData.complexities.overall.level
              ]
            }
            percent={complexitySiderData.complexities.overall.levelPercent}
            isClient={isClient}
          />
        </AccordionItemHeader>
        {getComplexItems()}
      </AccordionItem>
    ) : (
      <AccordionItem
        itemId="disabled-comp"
        className="disabled-complexity-menu"
      >
        <AccordionItemHeader>
          <ComplexityMenu title="Deal Complexity" noIndicator />
        </AccordionItemHeader>
      </AccordionItem>
    );

  useEffect(() => {
    dispatch(Actions.getComplexitySiderData(getSessionStorage('currentId')));
    return () => {
      dispatch(Actions.clearQuestions());
    };
  }, []);

  return (
    <div
      className={`section-sider-selector-wrapper ${
        isExpanded ? '' : 'is-collapsed'
      }`}
    >
      <Accordions activeItem={[activeMenuKey]} onChange={handleAccordionChange}>
        {canSeeQues ? (
          <AccordionItem itemId={MENU_KEYS.QUES}>
            <AccordionItemHeader>
              <QuestionnaireMenu
                lastEditedBy={attributes.lastEditedBy}
                lastEditedAt={attributes.lastEditedAt}
                isClient={isClient}
              />
            </AccordionItemHeader>
            {items}
            <AccordionItemBody
              onClick={() => {
                if (canSeeReviewResponses) {
                  history.push(`/questionnaires/review-responses`);
                }
              }}
              className={`${
                currentSection === 'review-responses' ? 'activated' : ''
              } section-review ${
                canSeeReviewResponses ? '' : 'disabled'
              } capitalize-title`}
            >
              review responses
            </AccordionItemBody>
          </AccordionItem>
        ) : (
          <div />
        )}
        {canSeeComplex ? renderComplexityMenu() : <div />}
      </Accordions>
    </div>
  );
}
SideProgressSection.propTypes = {};

export default SideProgressSection;
